/**
 * LeadPipelineMemberships
 *
 * Mostra tutte le pipeline a cui appartiene un lead/cliente (primaria + extra).
 * Stile Notion-like, coerente con LeadDesktopLayout.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { GitBranch, Plus, X } from 'lucide-react';
import { useClientPipelineMembershipStore } from '../../lib/stores/clientPipelineMembershipStore';
import { useLeadPipelineStore } from '../../lib/stores/leadPipelineStore';
import { useApp } from '../AppProvider';
import type { Client } from '../../lib/types';
import type { ClientPipelineMembership } from '../../lib/types/leadPipeline';

interface LeadPipelineMembershipsProps {
  client: Client;
  onUpdate?: () => void;
}

export const LeadPipelineMemberships: React.FC<LeadPipelineMembershipsProps> = ({ client, onUpdate }) => {
  const { toast } = useApp();
  const { pipelines, stages: stagesMap, fetchPipelines, fetchStagesByPipeline } = useLeadPipelineStore();
  const { fetchMembershipsForClient, addToPipeline, removeFromPipeline } = useClientPipelineMembershipStore();

  const [extraMemberships, setExtraMemberships] = useState<ClientPipelineMembership[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');
  const [selectedStageId, setSelectedStageId] = useState<string>('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchPipelines();
    fetchMembershipsForClient(client.id).then(setExtraMemberships);
  }, [client.id, fetchPipelines, fetchMembershipsForClient]);

  // Pipeline primaria
  const primaryPipeline = pipelines.find(p => p.id === client.pipeline_id);
  const primaryStages = client.pipeline_id ? (stagesMap.get(client.pipeline_id) || []) : [];
  const primaryStage = primaryStages.find(s => s.id === client.pipeline_stage_id);

  useEffect(() => {
    if (client.pipeline_id) fetchStagesByPipeline(client.pipeline_id);
  }, [client.pipeline_id, fetchStagesByPipeline]);

  // Pipeline in cui il lead è già presente
  const existingPipelineIds = new Set<string>();
  if (client.pipeline_id) existingPipelineIds.add(client.pipeline_id);
  extraMemberships.forEach(m => existingPipelineIds.add(m.pipeline_id));

  const availablePipelines = pipelines.filter(p => p.is_active && !existingPipelineIds.has(p.id));
  const addModalStages = selectedPipelineId ? (stagesMap.get(selectedPipelineId) || []) : [];

  useEffect(() => {
    if (selectedPipelineId) fetchStagesByPipeline(selectedPipelineId);
  }, [selectedPipelineId, fetchStagesByPipeline]);

  const handleAdd = useCallback(async () => {
    if (!selectedPipelineId || !selectedStageId) return;
    setAdding(true);
    try {
      const result = await addToPipeline(client.id, selectedPipelineId, selectedStageId);
      if (result) {
        toast.success('Aggiunto alla pipeline');
        setShowAddForm(false);
        setSelectedPipelineId('');
        setSelectedStageId('');
        const updated = await fetchMembershipsForClient(client.id);
        setExtraMemberships(updated);
        onUpdate?.();
      } else {
        toast.error('Errore nell\'aggiunta alla pipeline');
      }
    } catch {
      toast.error('Errore nell\'aggiunta alla pipeline');
    }
    setAdding(false);
  }, [client.id, selectedPipelineId, selectedStageId, addToPipeline, fetchMembershipsForClient, toast, onUpdate]);

  const handleRemoveExtra = useCallback(async (pipelineId: string) => {
    const success = await removeFromPipeline(client.id, pipelineId);
    if (success) {
      toast.success('Rimosso dalla pipeline');
      const updated = await fetchMembershipsForClient(client.id);
      setExtraMemberships(updated);
      onUpdate?.();
    } else {
      toast.error('Errore nella rimozione');
    }
  }, [client.id, removeFromPipeline, fetchMembershipsForClient, toast, onUpdate]);

  if (!client.pipeline_id && extraMemberships.length === 0) {
    return null;
  }

  const totalCount = (primaryPipeline ? 1 : 0) + extraMemberships.length;

  return (
    <>
      {/* Section Header — Notion style */}
      <div className="flex items-center justify-between py-2 mb-1">
        <div className="flex items-center space-x-2">
          <h3 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wide">Pipeline</h3>
          <span className="text-xs text-[#787774] dark:text-gray-500">({totalCount})</span>
        </div>
        {availablePipelines.length > 0 && (
          <button
            onClick={() => setShowAddForm(true)}
            className="text-xs text-[#337EA9] dark:text-[#529CCA] hover:underline"
            type="button"
          >
            + Aggiungi
          </button>
        )}
      </div>

      {/* Pipeline primaria — Notion PropertyRow style */}
      {primaryPipeline && (
        <div className="py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors">
          <div className="flex items-center space-x-2.5">
            <div
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: primaryPipeline.color }}
            />
            <span className="text-sm text-[#37352F] dark:text-white">{primaryPipeline.name}</span>
            {primaryStage && (
              <span
                className="text-xs px-1.5 py-0.5 rounded"
                style={{
                  backgroundColor: `${primaryStage.color}20`,
                  color: primaryStage.color
                }}
              >
                {primaryStage.name}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Pipeline extra — stessa struttura */}
      {extraMemberships.map(membership => {
        const pipelineData = membership.pipeline as any;
        const stageData = membership.pipeline_stage as any;
        return (
          <div
            key={membership.id}
            className="py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: pipelineData?.color || '#6B7280' }}
                />
                <span className="text-sm text-[#37352F] dark:text-white">
                  {pipelineData?.name || 'Pipeline'}
                </span>
                {stageData?.name && (
                  <span
                    className="text-xs px-1.5 py-0.5 rounded"
                    style={{
                      backgroundColor: `${stageData.color || '#6B7280'}20`,
                      color: stageData.color || '#6B7280'
                    }}
                  >
                    {stageData.name}
                  </span>
                )}
              </div>
              <button
                onClick={() => handleRemoveExtra(membership.pipeline_id)}
                className="opacity-0 group-hover:opacity-100 text-[#787774] hover:text-[#E16259] dark:text-gray-500 dark:hover:text-red-400 transition-opacity p-0.5"
                title="Rimuovi da questa pipeline"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        );
      })}

      {/* Nessuna pipeline extra — messaggio vuoto Notion style */}
      {!primaryPipeline && extraMemberships.length === 0 && (
        <p className="py-2 px-2 text-sm text-[#787774] dark:text-gray-400 italic">
          Nessuna pipeline assegnata
        </p>
      )}

      {/* Form aggiunta inline — Notion style */}
      {showAddForm && (
        <div className="py-2 px-2 space-y-2">
          <select
            value={selectedPipelineId}
            onChange={(e) => { setSelectedPipelineId(e.target.value); setSelectedStageId(''); }}
            className="w-full text-sm px-2 py-1.5 rounded bg-transparent border border-[#E9E9E7] dark:border-gray-600 text-[#37352F] dark:text-white focus:border-[#337EA9] dark:focus:border-[#529CCA] focus:outline-none transition-colors"
          >
            <option value="">Seleziona pipeline...</option>
            {availablePipelines.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          {selectedPipelineId && (
            <select
              value={selectedStageId}
              onChange={(e) => setSelectedStageId(e.target.value)}
              className="w-full text-sm px-2 py-1.5 rounded bg-transparent border border-[#E9E9E7] dark:border-gray-600 text-[#37352F] dark:text-white focus:border-[#337EA9] dark:focus:border-[#529CCA] focus:outline-none transition-colors"
            >
              <option value="">Seleziona stage...</option>
              {addModalStages.filter(s => s.is_active).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={handleAdd}
              disabled={!selectedPipelineId || !selectedStageId || adding}
              className="text-xs text-[#337EA9] dark:text-[#529CCA] hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
              type="button"
            >
              {adding ? 'Aggiunta...' : 'Conferma'}
            </button>
            <span className="text-[#787774] dark:text-gray-500">|</span>
            <button
              onClick={() => { setShowAddForm(false); setSelectedPipelineId(''); setSelectedStageId(''); }}
              className="text-xs text-[#787774] dark:text-gray-400 hover:underline"
              type="button"
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </>
  );
};
